/**
 * Sort control (PRD "Sort control", §12.3).
 *
 * One control, whichever tab mounts it: the panel is given a sort and a setter and never reads the
 * UI state itself. In Posts that pair is the same `sort` the column headers and the Sort trigger
 * write; in Feed it is `feedSort` — the two tabs order independently (§12.3). Fields are exactly
 * the server-sortable column ids (`POSTS_SORT_KEYS`), so nothing can be requested that SQL cannot
 * order by, and they are spelled by `sortKeyLabel` — the same words the trigger shows, so the two
 * cannot read differently for the same selection.
 */

import { getPostsColumn, POSTS_SORT_KEYS, type PostsSortKey, type SortDir } from '@lw/shared';
import { cx } from '../../components';
import { sortKeyLabel, type ViewSort } from './viewState';

export type SortPanelProps = {
  sort: ViewSort;
  onChange: (next: ViewSort) => void;
  isGroup: boolean;
};

export function SortPanel({ sort, onChange, isGroup }: SortPanelProps) {
  // Group-only columns (`author`, `authorFollowers`) are not offered on a profile view.
  const keys = POSTS_SORT_KEYS.filter((key) => (getPostsColumn(key).groupOnly ? isGroup : true));

  return (
    <div className="sort-panel">
      <div className="field">
        <label className="field__label" htmlFor="view-sort-key">
          Sort by
        </label>
        <select
          id="view-sort-key"
          className="select"
          value={sort.key}
          onChange={(event) => onChange({ ...sort, key: event.target.value as PostsSortKey })}
        >
          {keys.map((key) => (
            <option key={key} value={key}>
              {sortKeyLabel(key)}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="field__label">Direction</span>
        <div className="row">
          {(['desc', 'asc'] as SortDir[]).map((dir) => (
            <button
              key={dir}
              type="button"
              className={cx('table-action', sort.dir === dir && 'is-active')}
              aria-pressed={sort.dir === dir}
              onClick={() => onChange({ ...sort, dir })}
            >
              <span className="table-action__label">
                {dir === 'desc' ? 'Descending' : 'Ascending'}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SortPanel;
